import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useTranslation } from 'react-i18next';

import { Box } from './Box';
import { Icon } from './Icon';
import { ToggleButton } from './ToggleButton';
import { ToggleButtonGroup } from './ToggleButtonGroup';
import { Tooltip } from './Tooltip';
import { Typography } from './Typography';

type DeliveryMethod = 'in-app' | 'email' | 'sms';

interface DeliveryMethodTogglesProps {
  value: DeliveryMethod[];
  onChange: (value: DeliveryMethod[]) => void;
}

export const DeliveryMethodToggles: React.FC<DeliveryMethodTogglesProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'deliveryMethod' });
  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: DeliveryMethod[]
  ) => {
    // Ensure at least one delivery method is selected
    if (newValue.length > 0) {
      onChange(newValue);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('title')}
      </Typography>
      <ToggleButtonGroup
        value={value}
        onChange={handleChange}
        aria-label={t('ariaLabel')}
        exclusive={false}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 1,
          '& .MuiToggleButton-root': {
            flex: 1,
            p: 0.75,
            minWidth: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            '&.Mui-selected': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            },
            '&:hover': {
              bgcolor: 'action.hover',
              borderColor: 'primary.main',
            },
          },
        }}
      >
        <Tooltip
          title={t('inApp')}
          arrow
          PopperProps={{ sx: { zIndex: 1500 } }}
        >
          <Box component="span">
            <ToggleButton value="in-app" aria-label={t('inApp')}>
              <Icon
                icon={icon({ name: 'bell', style: 'regular' })}
                size="small"
              />
            </ToggleButton>
          </Box>
        </Tooltip>
        <Tooltip
          title={t('email')}
          arrow
          PopperProps={{ sx: { zIndex: 1500 } }}
        >
          <Box component="span">
            <ToggleButton value="email" aria-label={t('email')}>
              <Icon
                icon={icon({ name: 'envelope', style: 'regular' })}
                size="small"
              />
            </ToggleButton>
          </Box>
        </Tooltip>
        <Tooltip title={t('sms')} arrow PopperProps={{ sx: { zIndex: 1500 } }}>
          <Box component="span">
            <ToggleButton value="sms" aria-label={t('sms')}>
              <Icon
                icon={icon({ name: 'message', style: 'regular' })}
                size="small"
              />
            </ToggleButton>
          </Box>
        </Tooltip>
      </ToggleButtonGroup>
    </Box>
  );
};
